import React, { useState, useEffect, useRef } from 'react';
import VehicleForm from './VehicleForm';
import VehicleCard from './VehicleCard';
import './Vehicles.css';

const Vehicles = ({ user }) => {
  const [vehicles, setVehicles] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlant, setFilterPlant] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchVehicles();
    fetchPlants();
  }, []);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/vehicles.php', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setVehicles(data.data || []);
      } else {
        setError('Failed to fetch vehicles');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/plants.php', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setPlants(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch plants for filter:', err);
    }
  };

  const handleAddVehicle = () => {
    setShowForm(true);
  };

  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResults(null);
    setImportFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImportLoading(true);
    setError('');
    
    const formData = new FormData();
    formData.append('excel_file', importFile);
    formData.append('action', 'import');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/vehicles.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        setImportResults(data);
        setSuccess(`Import completed: ${data.summary.successful} successful, ${data.summary.failed} failed, ${data.summary.duplicates} duplicates`);
        setTimeout(() => setSuccess(''), 5000);
        fetchVehicles();
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (error) {
      setError('Error during import');
      console.error('Import error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['VehicleNumber', 'Type', 'DriverName', 'DriverPhone', 'Vendor', 'PlantName'],
      ['KA01AB1234', 'Truck', 'John Doe', '9876543210', 'ABC Logistics', 'HPCL PURNIA'],
      ['KA02CD5678', 'Container', 'Jane Smith', '9876543211', 'XYZ Transport', 'HPCL ARAH'],
      ['MH03EF9012', 'Trailer', 'Bob Johnson', '9876543212', 'Logistics Corp', 'HPCL PANAGARH']
    ];
    
    let csvContent = "data:text/csv;charset=utf-8,";
    templateData.forEach(row => {
      csvContent += row.join(",") + "\r\n";
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "vehicle_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFormClose = (added) => {
    setShowForm(false);
    if (added) {
      setSuccess('Vehicle added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchVehicles();
    }
  };

  const handleVehicleUpdate = () => {
    fetchVehicles();
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + `/vehicles.php?id=${vehicleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess('Vehicle deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
        fetchVehicles();
      } else {
        setError(data.message || 'Failed to delete vehicle');
      }
    } catch (error) {
      setError('Error deleting vehicle');
      console.error('Delete error:', error);
    }
  };

  const stats = {
    total: vehicles.length,
    boarded: vehicles.filter(v => v.current_status === 'boarded').length,
    unboarded: vehicles.filter(v => v.current_status === 'unboarded').length,
    overdue: vehicles.filter(v => v.current_status === 'boarded' && v.time_info?.is_overdue === true).length
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      (vehicle.vehicle_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vehicle.driver_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vehicle.vendor || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;

    if (filterStatus === 'all') {
      matchesStatus = true;
    } else if (filterStatus === 'boarded') {
      matchesStatus = vehicle.current_status === 'boarded';
    } else if (filterStatus === 'unboarded') {
      matchesStatus = vehicle.current_status === 'unboarded';
    } else if (filterStatus === 'overdue') {
      matchesStatus = vehicle.current_status === 'boarded' && vehicle.time_info?.is_overdue === true;
    }

    const matchesPlant = 
      filterPlant === 'all' || 
      String(vehicle.plant_id) === String(filterPlant);
    
    return matchesSearch && matchesStatus && matchesPlant;
  });

  return (
    <div className="vehicles-container">
      <div className="vehicles-header">
        <div>
          <h1>Vehicle Management</h1>
          <p className="page-subtitle">Manage all vehicles and track their boarding status</p>
        </div>
        <div className="header-actions">
          {user?.role === 'admin' && (
            <button className="btn-secondary" onClick={handleImportClick}>
              <span>📤</span> Import Excel
            </button>
          )}
          <button className="btn-primary" onClick={handleAddVehicle}>
            <span>+</span> Add Vehicle
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <span>❌</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span> {success}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card total">
          <div className="stat-icon">🚛</div>
          <div className="stat-info">
            <h3>Total Vehicles</h3>
            <p>{stats.total}</p>
          </div>
        </div>
        <div className="stat-card boarded">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>Boarded</h3>
            <p>{stats.boarded}</p>
          </div>
        </div>
        <div className="stat-card unboarded">
          <div className="stat-icon">⏸️</div>
          <div className="stat-info">
            <h3>Unboarded</h3>
            <p>{stats.unboarded}</p>
          </div>
        </div>
        <div className="stat-card overdue">
          <div className="stat-icon">⏰</div>
          <div className="stat-info">
            <h3>Overdue</h3>
            <p>{stats.overdue}</p>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search vehicles by number, driver, or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({stats.total})
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'boarded' ? 'active' : ''}`}
            onClick={() => setFilterStatus('boarded')}
          >
            Boarded ({stats.boarded})
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'unboarded' ? 'active' : ''}`}
            onClick={() => setFilterStatus('unboarded')}
          >
            Unboarded ({stats.unboarded})
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'overdue' ? 'active' : ''}`}
            onClick={() => setFilterStatus('overdue')}
          >
            Overdue ({stats.overdue})
          </button>
        </div>

        {plants.length > 0 && (
          <div className="plant-filter">
            <label htmlFor="plant-filter-select">Plant:</label>
            <select
              id="plant-filter-select"
              value={filterPlant}
              onChange={(e) => setFilterPlant(e.target.value)}
              className="plant-select"
            >
              <option value="all">All Plants</option>
              {plants.map(plant => (
                <option key={plant.id} value={plant.id}>
                  {plant.plant_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading vehicles...</p>
        </div>
      ) : filteredVehicles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚛</div>
          <h3>No vehicles found</h3>
          <p>
            {searchTerm || filterPlant !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting filters or search term'
              : 'Add your first vehicle to get started'}
          </p>
          {!searchTerm && filterPlant === 'all' && filterStatus === 'all' && (
            <div className="empty-state-actions">
              <button className="btn-primary" onClick={handleAddVehicle}>
                Add First Vehicle
              </button>
              {user?.role === 'admin' && (
                <button className="btn-secondary" onClick={handleImportClick}>
                  Import from Excel
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="vehicles-grid">
          {filteredVehicles.map(vehicle => (
            <VehicleCard 
              key={vehicle.id} 
              vehicle={vehicle} 
              onUpdate={handleVehicleUpdate}
              onDelete={handleDeleteVehicle}
              userRole={user?.role}
            />
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <VehicleForm onClose={handleFormClose} />
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal import-modal">
            <div className="modal-header">
              <h2>Import Vehicles from Excel/CSV</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                  setImportFile(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="file-upload-section">
                <h3>📁 Upload File</h3>
                <div 
                  className={`file-drop-area ${importFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />
                  
                  {importFile ? (
                    <div className="file-selected">
                      <span className="file-icon">📄</span>
                      <div className="file-info">
                        <strong>{importFile.name}</strong>
                        <small>{(importFile.size / 1024).toFixed(2)} KB</small>
                      </div>
                      <button 
                        className="remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImportFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="upload-icon">📤</span>
                      <p>Click to select or drag & drop Excel/CSV file</p>
                      <p className="file-types">Supported: .xlsx, .xls, .csv</p>
                    </>
                  )}
                </div>
              </div>
              
              {importResults && (
                <div className="import-results">
                  <h3>📊 Import Results</h3>
                  <div className="results-summary">
                    <div className="result-stat success">
                      <span>✅</span>
                      <div>
                        <h4>Successful</h4>
                        <p>{importResults.summary.successful}</p>
                      </div>
                    </div>
                    <div className="result-stat failed">
                      <span>❌</span>
                      <div>
                        <h4>Failed</h4>
                        <p>{importResults.summary.failed}</p>
                      </div>
                    </div>
                    <div className="result-stat duplicate">
                      <span>⚠️</span>
                      <div>
                        <h4>Duplicates</h4>
                        <p>{importResults.summary.duplicates}</p>
                      </div>
                    </div>
                  </div>
                  
                  {importResults.details && importResults.details.some(d => d.status === 'error') && (
                    <div className="error-details">
                      <h4>Failed Rows:</h4>
                      <div className="error-list">
                        {importResults.details
                          .filter(d => d.status === 'error')
                          .map((detail, index) => (
                            <div key={index} className="error-item">
                              <span>Row {detail.row}:</span> {detail.vehicle_number} - {detail.message}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="import-instructions">
                <h3>📋 Instructions:</h3>
                <ol>
                  <li>Download the template below to see the required format</li>
                  <li>Fill in your vehicle data with columns: <strong>VehicleNumber, Type, DriverName, DriverPhone, Vendor, PlantName</strong></li>
                  <li>Upload your Excel (.xlsx, .xls) or CSV file</li>
                  <li>Review and confirm import</li>
                </ol>
                
                <div className="template-section">
                  <button className="btn-secondary" onClick={downloadTemplate}>
                    📥 Download Template
                  </button>
                  <p className="template-note">Use the template to ensure proper formatting</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                  setImportFile(null);
                }}
                disabled={importLoading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleImport}
                disabled={!importFile || importLoading}
              >
                {importLoading ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Importing...
                  </>
                ) : (
                  'Import Vehicles'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;